// Reversibility seam (spec §4). All chain ops go through this one interface so
// swapping chains = swapping the adapter + config, never rewriting UI or the
// Worker contracts. CircleArcAdapter (v3.1) and a retained StellarPasskeyAdapter
// (v2) both implement it.

export type ConnectMode = "register" | "login";

/** v0.90.26 — what the bundler's acceptance gives back, and the receipt to follow. */
export interface Submitted {
  userOpHash: string;
  settled: Promise<{ txHash: string; success: boolean }>;
}

/** v0.90.29 — a userOp with everything but the signature; opaque to the flows.
 *  v2.0 (app 1.4.78+) — prepared with the paymaster's STUB only: nothing at
 *  Circle is reserved for it. The sponsor's signature is asked for by
 *  sponsor(), on the touch that starts the slide (or by sendPrepared itself). */
/** 1.5.42 — a sponsored, unsigned userOp in the RPC shape, with its hash. */
export interface ExportedOp {
  chainId: number;
  entryPoint: `0x${string}`;
  op: import("../cardApproval").HandoffOp;
  hash: `0x${string}`;
}
export interface PreparedOp {
  request: unknown;
  at: number; // Date.now() when prepared
}
/** How long a prepared (unsponsored) userOp is trusted: its nonce is checked
 *  again at sponsor time, so this only bounds the gas estimate and the fees.
 *  v2.0 — was 60 s while the prepare carried Circle's signature. */
export const PREPARED_TTL_MS = 5 * 60_000;
/** How long a sponsorship asked for on touch is used before it is asked for again. */
export const SPONSOR_TTL_MS = 60_000;

/** The PUBLIC parts of a WebAuthn credential — safe to persist (no private key). */
export interface WalletCredential {
  id: string;
  publicKey: string;
  rpId?: string;
  /** 1.5.44 — the passkey's user handle (base64url): public, read back at every login; lets M
   *  tell the phone's password manager the wallet's new name (passkeyName.ts). */
  userHandle?: string;
}

export interface WalletAdapter {
  /**
   * Register a brand-new passkey (mode="register", userLabel required — it is
   * just a credential label shown in the iOS passkey picker), or reconnect an
   * existing wallet via the usernameless discoverable-passkey ceremony
   * (mode="login"). The MSCA address is deterministic, so login reproduces the
   * same wallet with no server lookup. Returns the credential's public values so
   * the caller can cache them for a later silent reconnect.
   */
  createOrConnect(
    mode: ConnectMode,
    userLabel?: string,
    displayName?: string | null, // v0.70.12 — passkey list label "M® · Name"
    pin?: string, // v1.1.6 — login only: this browser's credential id (allow that passkey only)
  ): Promise<{ address: string; credential: WalletCredential }>;

  /**
   * Rebuild the smart account from a previously cached credential WITHOUT any
   * biometric ceremony (no login prompt). Spending still requires a signing
   * biometric in transfer(), so this is safe — it only skips the redundant
   * "which wallet are you" step. Enables single-Face-ID payments.
   */
  connectWithCredential(
    credential: WalletCredential,
  ): Promise<{ address: string }>;

  /** 1.5.58 — the account a signature would come from now (null when none is connected): checked before a signature,
   *  since a background connect (a warm-up) may have moved it. Optional: an adapter without it isn't checked. */
  connectedAddress?(): string | null;

  /**
   * Send money. Face ID is prompted at signing; Circle's paymaster covers gas.
   * v0.80.28 — `currency` picks the ERC-20 (USD → USDC, EUR → EURC); omitted
   * means USD, so every existing caller is unchanged.
   */
  transfer(to: string, amount: string, currency?: string): Promise<{ txHash: string }>;

  /**
   * v0.90.26 — success at acceptance. Same as transfer()/execute() but returns
   * the moment the bundler ACCEPTS the userOp (Face ID already given): the
   * userOp hash now, and a promise for the receipt that the caller may await
   * in the background or ignore. A submitted userOp proceeds whether or not
   * the browser stays open; `settled` only reports. Swaps keep using execute()
   * because their card shows the settled figure.
   */
  submitTransfer(to: string, amount: string, currency?: string): Promise<Submitted>;
  submit(calls: { to: string; data: string }[]): Promise<Submitted>;

  /**
   * v0.90.29 — prepare before the slide. Everything that costs a round trip
   * (nonce, gas estimate with the paymaster's stub, fee levels) is done while the
   * pass card is on screen; nothing is signed and nothing is submitted. The
   * slide's release then calls sendPrepared(): Face ID at once, then the
   * bundler. A prepared userOp that has gone stale (older than PREPARED_TTL_MS,
   * or the calls changed) is simply discarded and the plain submit path runs.
   */
  prepareTransfer(to: string, amount: string, currency?: string): Promise<PreparedOp>;
  prepare(calls: { to: string; data: string }[]): Promise<PreparedOp>;
  /**
   * v2.0 — sponsor on touch (22 Oct 2026: every pm_getPaymasterData reserves
   * gas limits × maxFeePerGas against Circle's daily cap; the stub reserves
   * nothing). Asks Circle's paymaster to sign this prepared op and checks its
   * nonce, in parallel. Called when the finger lands on the thumb, so it is
   * done by the slide's end; a second call within SPONSOR_TTL_MS reuses the
   * first. Rejects with "prepared_stale" when the nonce has moved (the caller
   * takes the plain path), or with the paymaster's own refusal.
   */
  sponsor(p: PreparedOp): Promise<void>;
  /**
   * 1.5.42 — the M® card's round trip (card.ts, cardApproval.ts). exportSponsored() sponsors the prepared op and
   * hands it out in the RPC shape (hex strings) with its hash — what M's tab gives the approver and keeps in
   * sessionStorage while the tab is away. sendExported() takes that shape back after the trip: the passkey signs it
   * (Face ID), then the bundler. Nothing in between can change the op without changing the hash the card signed.
   */
  exportSponsored(p: PreparedOp): Promise<ExportedOp>;
  sendExported(x: ExportedOp): Promise<Submitted>;
  /**
   * 1.5.47 (Alexander, 31 Oct) — Apple Pay's order: the phone signs FIRST, on the Pay tap (Face ID at once, no system
   * interstitial), then the trip to the approver for the card, and on return the signed op goes to the bundler with no
   * further prompt. signExported() is the passkey's signature over the exported op's hash; sendSigned() submits it.
   */
  signExported(x: ExportedOp): Promise<`0x${string}`>;
  sendSigned(x: ExportedOp, signature: `0x${string}`): Promise<Submitted>;
  /** Face ID, then the bundler. Sponsors first if the touch didn't (or too long ago). */
  sendPrepared(p: PreparedOp): Promise<Submitted>;

  /** Human-readable balance for an address, in `currency` (default USD). */
  getBalance(address: string, currency?: string): Promise<string>;

  /**
   * v0.30 — read a uint256 from any contract on the same chain (e.g. M-MMF
   * balanceOf, token index). No signing, no biometric.
   */
  readUint(
    contract: string,
    abi: readonly unknown[],
    functionName: string,
    args: readonly unknown[],
  ): Promise<bigint>;

  /**
   * v0.30 — sign & submit a batch of contract calls as one UserOp (one Face ID,
   * paymaster-sponsored). Used for subscribe (approve + subscribe) and redeem.
   * `calls` are {to, data} produced by the caller with encodeFunctionData.
   */
  execute(
    calls: { to: string; data: string }[],
  ): Promise<{ txHash: string }>;

  /**
   * v1.3.10 — the spare key (src/sparekey).
   *
   * read: any view call on the same chain; no signing.
   * submitRaw: one UserOp whose callData is given whole — NOT wrapped in
   *   execute(). Adding or removing a key of the wallet only passes this way
   *   (sparekey/owners.ts). The passkey signs: one Face ID.
   * submitWithKey: `calls` as one UserOp signed by an ordinary key that is
   *   already on the wallet (a spare key), sponsored like any other. No
   *   Face ID. The key is used for the signature and dropped.
   */
  read(
    contract: string,
    abi: readonly unknown[],
    functionName: string,
    args: readonly unknown[],
  ): Promise<unknown>;
  submitRaw(callData: string): Promise<Submitted>;
  submitWithKey(privateKey: string, calls: { to: string; data: string }[]): Promise<Submitted>;

  /** Explorer link for a tx hash. */
  explorerUrl(txHash: string): string;
}

/**
 * v2.0 — the touch that starts a slide (or a tap on a take / claim button):
 * ask for the sponsor's signature of whatever is prepared, once it is. Never
 * throws — sendPrepared asks again and says what went wrong. Takes the
 * prepared op itself, a promise for it, or something that holds one.
 */
export function sponsorOnTouch(
  adapter: Pick<WalletAdapter, "sponsor">,
  p: PreparedOp | null | undefined | Promise<PreparedOp | null | undefined>,
): void {
  void Promise.resolve(p)
    .then((op) => (op && Date.now() - op.at < PREPARED_TTL_MS ? adapter.sponsor(op) : undefined))
    .catch(() => undefined);
}
